import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, MapPin, Search } from "lucide-react";
import { api } from "../lib/api";
import { indianState } from "../lib/indianStates";
import { loadGoogleMapsLibrary, loadGooglePlacesLibrary, loadGoogleGeocodingLibrary, type GoogleMapsAddressComponent, type GoogleMapsAutocompleteSessionToken, type GoogleMapsMap, type GoogleMapsPlacePrediction } from "../lib/googleMaps";
import "./AddressLocationPicker.css";

export type PickedAddress = {
  latitude: number; longitude: number; placeId: string | null; name: string;
  addressLine1: string; city: string; stateName: string; stateCode: string; postalCode: string; countryCode: string;
};
type LocationValue = { latitude: number | null; longitude: number | null; placeId: string | null };

export function addressFromComponents(components: GoogleMapsAddressComponent[], formatted: string, name: string, latitude: number, longitude: number, placeId: string | null): PickedAddress {
  const value = (type: string, short = false) => {
    const component = components.find(item => item.types.includes(type));
    return (short ? component?.shortText : component?.longText) ?? "";
  };
  const countryCode = value("country", true) || "IN";
  const stateName = value("administrative_area_level_1");
  const state = countryCode === "IN" ? indianState(stateName) : undefined;
  const street = [value("premise"), value("street_number"), value("route"), value("sublocality_level_1")].filter(Boolean).join(", ");
  return { latitude, longitude, placeId, name, countryCode, addressLine1: (street || formatted).slice(0, 255),
    city: value("locality") || value("postal_town") || value("administrative_area_level_3") || value("administrative_area_level_2"),
    stateName: state?.name ?? stateName, stateCode: state?.code ?? "", postalCode: value("postal_code") };
}

export function AddressLocationPicker({ value, initialName = "", disabled = false, onChange }: {
  value: LocationValue; initialName?: string; disabled?: boolean; onChange: (address: PickedAddress | null) => void;
}) {
  const listId = useId();
  const [query, setQuery] = useState(initialName);
  const [predictions, setPredictions] = useState<GoogleMapsPlacePrediction[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mapElement = useRef<HTMLDivElement>(null);
  const map = useRef<GoogleMapsMap | null>(null);
  const selectedText = useRef(initialName);
  const session = useRef<GoogleMapsAutocompleteSessionToken | null>(null);
  const generation = useRef(0);
  const current = useRef({ value, onChange, disabled });
  current.current = { value, onChange, disabled };
  const config = useQuery({ queryKey: ["google-maps-configuration"], queryFn: api.getGoogleMapsConfiguration, staleTime: Infinity, retry: false });
  const libraries = useQuery({ queryKey: ["address-location-libraries"], enabled: Boolean(config.data?.api_key), staleTime: Infinity, retry: false,
    queryFn: async () => {
      const [maps, places, geocoding] = await Promise.all([loadGoogleMapsLibrary(config.data!.api_key), loadGooglePlacesLibrary(config.data!.api_key), loadGoogleGeocodingLibrary(config.data!.api_key)]);
      return { maps, places, geocoding };
    }
  });
  const lookupRef = useRef<(latitude: number, longitude: number) => void>(() => undefined);
  lookupRef.current = (latitude, longitude) => { void pickCoordinates(latitude, longitude); };

  useEffect(() => {
    if (!libraries.data || !mapElement.current) return;
    const location = current.current.value;
    const selected = location.latitude !== null && location.longitude !== null;
    const instance = new libraries.data.maps.Map(mapElement.current, {
      center: selected ? { lat: location.latitude, lng: location.longitude } : { lat: 20.5937, lng: 78.9629 },
      zoom: selected ? 16 : 5, streetViewControl: false, mapTypeControl: false, fullscreenControl: false, gestureHandling: "cooperative"
    });
    map.current = instance;
    const listener = instance.addListener("click", event => {
      if (event.latLng && !current.current.disabled) lookupRef.current(event.latLng.lat(), event.latLng.lng());
    });
    const dragListener = instance.addListener("dragstart", () => {
      if (!current.current.disabled) { generation.current += 1; setBusy(false); current.current.onChange(null); }
    });
    return () => { dragListener.remove(); listener.remove(); map.current = null; generation.current += 1; };
  }, [libraries.data]);

  useEffect(() => {
    let cancelled = false;
    if (!libraries.data || query.trim().length < 3 || query === selectedText.current || disabled) {
      setPredictions([]); setSearching(false); return;
    }
    setSearching(true);
    const timer = window.setTimeout(() => {
      session.current ??= new libraries.data!.places.AutocompleteSessionToken();
      void libraries.data!.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: query, sessionToken: session.current, language: "en", region: "in" })
        .then(result => { if (!cancelled) { setPredictions(result.suggestions.flatMap(item => item.placePrediction ? [item.placePrediction] : [])); setActiveIndex(-1); } })
        .catch(() => { if (!cancelled) setError("Search is unavailable. Choose a point on the map or try again."); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, libraries.data, disabled]);

  function apply(address: PickedAddress) {
    selectedText.current = address.name || address.addressLine1 || "Selected map location";
    setQuery(selectedText.current); setPredictions([]); setActiveIndex(-1);
    map.current?.setCenter({ lat: address.latitude, lng: address.longitude });
    map.current?.setZoom(16);
    current.current.onChange(address);
    session.current = null;
  }
  async function choose(prediction: GoogleMapsPlacePrediction) {
    const request = ++generation.current;
    setBusy(true); setError(null); current.current.onChange(null); setPredictions([]);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["location", "addressComponents", "formattedAddress", "displayName", "id"] });
      if (request !== generation.current) return;
      if (!place.location) throw new Error("This result has no map location. Choose another place or use the map.");
      apply(addressFromComponents(place.addressComponents ?? [], place.formattedAddress ?? "", place.displayName ?? prediction.text.text ?? "", place.location.lat(), place.location.lng(), place.id ?? prediction.placeId));
    } catch (cause) { if (request === generation.current) setError(cause instanceof Error ? cause.message : "Could not select this location. Try again."); }
    finally { if (request === generation.current) setBusy(false); }
  }
  async function pickCoordinates(latitude: number, longitude: number) {
    if (!libraries.data || current.current.disabled) return;
    const request = ++generation.current;
    setBusy(true); setError(null); current.current.onChange(null); setPredictions([]);
    map.current?.setCenter({ lat: latitude, lng: longitude });
    try {
      const result = await new libraries.data.geocoding.Geocoder().geocode({ location: { lat: latitude, lng: longitude } });
      if (request !== generation.current) return;
      const address = result.results[0];
      apply(addressFromComponents(address?.address_components.map(item => ({ longText: item.long_name, shortText: item.short_name, types: item.types })) ?? [],
        address?.formatted_address ?? "", address?.formatted_address?.split(",")[0] ?? "Selected map location", latitude, longitude, address?.place_id ?? null));
      if (!address) setError("Location selected. Enter the address details below; no address was found for this pin.");
    } catch {
      if (request !== generation.current) return;
      apply(addressFromComponents([], "", "Selected map location", latitude, longitude, null));
      setError("Location selected. Address lookup is unavailable; enter the details below.");
    } finally { if (request === generation.current) setBusy(false); }
  }
  const selected = value.latitude !== null && value.longitude !== null;
  const loadError = config.isError || libraries.isError;
  return <div className="address-location-picker">
    <label htmlFor={`${listId}-search`} className="address-location-label">Find the address on the map <span>Required</span></label>
    <div className="address-location-search workspace-search">
      <Search size={18} aria-hidden="true" />
      <input id={`${listId}-search`} role="combobox" aria-autocomplete="list" aria-controls={listId} aria-expanded={predictions.length > 0}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined} autoComplete="off" value={query}
        placeholder="Search a building, street or area" disabled={disabled || !libraries.data}
        onChange={event => { generation.current += 1; setBusy(false); setQuery(event.target.value); setError(null); onChange(null); }}
        onKeyDown={event => {
          if (event.key === "ArrowDown") { event.preventDefault(); setActiveIndex(index => Math.min(index + 1, predictions.length - 1)); }
          if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex(index => Math.max(0, index - 1)); }
          if (event.key === "Escape") { setPredictions([]); setActiveIndex(-1); }
          if (event.key === "Enter") { event.preventDefault(); if (predictions[activeIndex]) void choose(predictions[activeIndex]); }
        }} />
      {busy || searching ? <Loader2 size={17} className="spin" aria-label="Finding location" /> : null}
      {predictions.length ? <ul id={listId} role="listbox" className="address-location-results">{predictions.map((prediction, index) =>
        <li key={prediction.placeId} id={`${listId}-${index}`} role="option" aria-selected={index === activeIndex}>
          <button type="button" tabIndex={-1} onClick={() => void choose(prediction)}><MapPin size={16} /><span>{prediction.text.text ?? prediction.text.toString?.()}</span></button>
        </li>)}</ul> : null}
    </div>
    <div className="address-location-map-frame">
      <div ref={mapElement} className="address-location-map" aria-label="Address location map" />
      {libraries.data ? <MapPin size={32} className="address-location-center-pin" aria-hidden="true" /> :
        <div className="address-location-loading" role="status">{loadError ? "The map could not load. Check your connection and try again." : <><Loader2 size={22} className="spin" /> Loading map…</>}</div>}
    </div>
    {loadError ? <button type="button" className="pa-link-button" onClick={() => { if (config.data?.api_key) void libraries.refetch(); else void config.refetch(); }}>Retry map</button> : null}
    <p className="address-location-status" aria-live="polite">{selected ? <><Check size={15} /> Map location selected</> : "Search and choose a result, or click the map to place a pin."}</p>
    {error ? <p className="pa-form-error" role="alert">{error}</p> : null}
  </div>;
}
